import { useState } from 'react';

import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import { TONES } from '../billing/TonePill';
import { HOURS_PATTERN, formatHours } from '../money';
import type { ResolveOutcome } from './ResolveExpander';

/**
 * **Everything partial-confirmation, quarantined in one module.**
 *
 * Approver-side partial confirm is live and QA-verified: the api accepts
 * `mode: 'partial'` with `0 < hoursConfirmed < hoursSubmitted` and a required
 * note, and `partially_confirmed` is a real status on `main`. What is *not*
 * settled is the expert's half — every outbound edge from `partially_confirmed`
 * is deferred pending a product ruling, so the status is terminal here and no
 * expert affordance is built on it whichever way that ruling lands.
 *
 * This file exists so that ruling is cheap either way. Everything partial is
 * here or in `partialConfirm.test.tsx` beside it, and the rest of the feature
 * holds it at five sites: `ConfirmActions` (the action), `ResolveExpander` (the
 * fourth outcome, plus the bound and the helper copy it borrows from here),
 * `HoursStatusCell` (the status pill; the props for both are threaded down
 * through `HoursPanel` and `HoursRowActions`), `HoursRow` (the approver rule),
 * and the `PartialConfirm` story.
 *
 * Rehearsed rather than asserted: deleting the two files and unwinding those
 * four sites is **537 lines out, 9 back in**, with 279/279 remaining tests
 * green. Seven of the nine are in `ResolveExpander`, which gives up
 * `checkPartialHours` and needs its own hours validation again; restore its
 * helper as `formatHours(maxConfirmableHours)` rather than the raw
 * interpolation it carried before, or the deletion puts float noise back in the
 * copy.
 *
 * Nothing partial may be added anywhere else.
 */

/** Site 2: spread into `ResolveExpander`'s outcome list. */
export const PARTIAL_RESOLVE_OUTCOME: ResolveOutcome = {
  value: 'partially_confirmed',
  label: 'Confirm part of it',
  needsHours: true,
};

/**
 * `maxConfirmableHours` is `min(submitted, nteHoursTotal - nteHoursConsumed)`,
 * and that subtraction is binary floating point: 10 − 9.7 lands on
 * `0.2999999999999998`, a hundredth below the value the api holds. Rounding to
 * the wire's two places recovers it, so the bound the field enforces is the
 * bound the helper copy advertises rather than an unreachable sliver under it.
 */
function toWireHours(hours: number): number {
  return Math.round(hours * 100) / 100;
}

/**
 * The api's partial bound, applied client-side before the round-trip:
 * `0 < hoursConfirmed < hoursSubmitted` (strictly less — a "partial" equal to
 * the submission is answered with `invalid_confirmed_hours`), capped by NTE
 * headroom. The input must be a well-formed `Hours` scalar first — the same
 * pattern `SubmitHoursForm` holds the expert to — and the bound is then applied
 * to the two-decimal string that actually goes on the wire.
 */
export function checkPartialHours(
  hoursText: string,
  bounds: { hoursSubmitted: number; maxConfirmableHours: number }
): { valid: boolean; hoursConfirmed: string } {
  const trimmed = hoursText.trim();
  if (!HOURS_PATTERN.test(trimmed)) return { valid: false, hoursConfirmed: '' };
  const hoursConfirmed = Number.parseFloat(trimmed).toFixed(2);
  const value = Number(hoursConfirmed);
  return {
    valid:
      value > 0 &&
      value < bounds.hoursSubmitted &&
      value <= toWireHours(bounds.maxConfirmableHours),
    hoursConfirmed,
  };
}

/** Helper copy for the bound: the NTE cap when it bites, the strict bound when it doesn't. */
export function partialHoursHelperText(bounds: {
  hoursSubmitted: number;
  maxConfirmableHours: number;
}): string {
  const cap = toWireHours(bounds.maxConfirmableHours);
  return cap < bounds.hoursSubmitted
    ? `Up to ${formatHours(cap)} of the ${formatHours(bounds.hoursSubmitted)} submitted.`
    : `Less than the ${formatHours(bounds.hoursSubmitted)} submitted.`;
}

/**
 * Site 3: spread into `HoursStatusCell`'s status-pill map, with the prototype's
 * "Partial" label and the yellow of a row still short of its hours.
 *
 * Note for whoever executes the deletion: `partially_confirmed` is a persisted
 * status, so rows already in it survive the ruling. Removing this entry leaves
 * them rendering the raw `partially_confirmed` string through the map's
 * fallback — legible, but worth keeping the entry if any such rows exist.
 */
export const PARTIAL_STATUS_PILL = {
  partially_confirmed: { label: 'Partial', tone: TONES.awaiting },
};

/** Site 5: spread into `HoursRow`'s approved statuses, so a partial names its approver. */
export const PARTIAL_APPROVED_STATUSES = ['partially_confirmed'];

interface PartialConfirmActionProps {
  /** Largest partial that can be confirmed: min(submitted, NTE remaining). */
  maxConfirmableHours: number;
  hoursSubmitted: number;
  busy: boolean;
  onConfirm: (hoursConfirmed: string, note: string) => void;
}

/**
 * Site 1: the button and expander that sit beside Confirm-in-full.
 *
 * Both the hours and the note are required — the api rejects a partial without
 * either (`invalid_confirmed_hours`, `note_required`) — and the hours are bounded
 * client-side by the same rule the server applies, so the approver finds out
 * before the round-trip rather than after it.
 */
export function PartialConfirmAction({
  maxConfirmableHours,
  hoursSubmitted,
  busy,
  onConfirm,
}: PartialConfirmActionProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [hoursText, setHoursText] = useState('');
  const [note, setNote] = useState('');

  const { valid: hoursValid, hoursConfirmed } = checkPartialHours(hoursText, {
    hoursSubmitted,
    maxConfirmableHours,
  });
  const trimmedNote = note.trim();
  const canSubmit = hoursValid && trimmedNote.length > 0 && !busy;

  if (!open) {
    return (
      <Button variant="secondary" size="small" disabled={busy} onClick={() => setOpen(true)}>
        Confirm part
      </Button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-16">
      <div className="flex flex-col gap-2">
        <span className="text-on-surface subtitle-2">Confirm part of this submission</span>
        <span className="text-on-surface-muted body-2">
          The hours you confirm are billed; the rest are not. The expert sees your note.
        </span>
      </div>

      <Input
        label="Hours to confirm"
        value={hoursText}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHoursText(e.target.value)}
        error={hoursText !== '' && !hoursValid}
        helperText={partialHoursHelperText({ hoursSubmitted, maxConfirmableHours })}
      />

      <Input
        label="Why you're confirming less"
        multiline
        rows={3}
        value={note}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
        helperText="Required. The expert will see this note."
      />

      <div className="flex justify-end gap-8">
        <Button variant="tertiary" size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          size="small"
          disabled={!canSubmit}
          onClick={() => onConfirm(hoursConfirmed, trimmedNote)}
        >
          Confirm part
        </Button>
      </div>
    </div>
  );
}
